/**
 * Utilities for determining active state and auto-expansion of left nav items.
 *
 * The left nav contains collapsible sections (Me, Team, Company, Admin) whose
 * children link to various pages. Two challenges arise:
 *
 * 1. **Profile redirects**: Items under "Me" link to `/profile/*`, but the
 *    `MyProfile` component immediately redirects to `/people/<id>/*`. So the
 *    browser URL never actually matches the nav item's `path`. We resolve
 *    these by replacing the `/profile` prefix with the current user's URL
 *    (e.g., `/people/42`).
 *
 * 2. **Broad active paths**: Some items (e.g., "Surveys & performance") link
 *    to a specific sub-page (`/performance/admin`) but should appear active
 *    on sibling paths (`/performance/participation`). These items declare an
 *    `activePath` that serves as a broader prefix for matching.
 */

/** A nav child item with its path and optional matching overrides. */
export interface NavChildItem {
  path: string;
  /** If set, the item is considered active when the URL matches this prefix too. */
  activePath?: string;
  /** When false, the NavLink uses prefix matching instead of exact. */
  exact?: boolean;
}

/**
 * Returns true if `pathname` matches `target` exactly, or starts with `target/`.
 */
function pathMatchesOrIsSubPath(pathname: string, target: string): boolean {
  return pathname === target || pathname.startsWith(target + '/');
}

/**
 * Checks whether a nav item should be considered "active" for the given pathname.
 *
 * @param item       - The nav child item to check
 * @param pathname   - The current browser URL pathname
 * @param meUrl      - The current user's profile base URL (e.g., `/people/42`),
 *                     used to resolve `/profile/*` redirects. Pass undefined if unknown.
 */
export function isNavItemActive(
  item: NavChildItem,
  pathname: string,
  meUrl: string | undefined
): boolean {
  // Direct path match (e.g., /feedback/received matches /feedback)
  if (pathMatchesOrIsSubPath(pathname, item.path)) {
    return true;
  }

  // Broad activePath match (e.g., /performance/participation matches activePath=/performance)
  if (item.activePath && pathMatchesOrIsSubPath(pathname, item.activePath)) {
    return true;
  }

  // Resolve /profile/* → /people/<id>/* for "Me" section items
  if (meUrl && item.path.startsWith('/profile')) {
    const subroute = item.path.slice('/profile'.length); // e.g., '' or '/objectives'
    const resolvedPath = meUrl + subroute; // e.g., '/people/42/objectives'

    if (pathname === resolvedPath) {
      return true;
    }

    // Only allow sub-path matching when there's a subroute, to prevent
    // the overview item (/profile → /people/42) from matching all sub-pages
    if (subroute !== '' && pathname.startsWith(resolvedPath + '/')) {
      return true;
    }
  }

  return false;
}

/**
 * Finds the toggler ID of the nav section that should be auto-expanded
 * based on the current URL.
 *
 * Iterates through all menu items with children and returns the toggler ID
 * (e.g., `"site-nav-toggler-1"`) of the first section containing a child
 * that matches the current pathname.
 *
 * @param menuItems  - The top-level nav menu items (some with children arrays)
 * @param pathname   - The current browser URL pathname
 * @param meUrl      - The current user's profile base URL, for resolving /profile/* redirects
 * @returns The toggler ID string, or null if no section matches
 */
export function findAutoExpandedSection(
  menuItems: Array<{ children?: NavChildItem[] }>,
  pathname: string,
  meUrl: string | undefined
): string | null {
  for (let i = 0; i < menuItems.length; i++) {
    const item = menuItems[i];
    if (item.children) {
      for (const child of item.children) {
        if (isNavItemActive(child, pathname, meUrl)) {
          return 'site-nav-toggler-' + i;
        }
      }
    }
  }
  return null;
}

/**
 * Returns a custom `isActive` function for a react-router `<NavLink>`,
 * or `undefined` to use the default matching behavior.
 *
 * This is needed because:
 * - `/profile/*` items redirect to `/people/<id>/*`, so the default
 *   NavLink path matching never activates them.
 * - Items with `activePath` need to match a broader URL prefix than
 *   their actual link target.
 *
 * @param item  - The nav child item
 * @param meUrl - The current user's profile base URL
 * @returns An isActive callback for NavLink, or undefined for default behavior
 */
export function getNavLinkIsActive(
  item: NavChildItem,
  meUrl: string | undefined
): ((_match: unknown, location: { pathname: string }) => boolean) | undefined {
  // Items with activePath need custom matching
  if (item.activePath) {
    return (_match, loc) => isNavItemActive(item, loc.pathname, meUrl);
  }

  // /profile/* items need custom matching to resolve the redirect
  if (meUrl && item.path.startsWith('/profile')) {
    return (_match, loc) => isNavItemActive(item, loc.pathname, meUrl);
  }

  // All other items use default NavLink matching
  return undefined;
}
